import _defineProperty from "/home/circleci/project/website/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable jsdoc/require-example */
import axios from 'axios';
import { omit, noop } from 'lodash/fp';
import { formatDatetimeServer, createUuid, toBase64 } from '../utils';
var DEFAULT_CONFIG = {
  token: null,
  apiUrl: '/api/2.0/js_proxy/mp',
  debug: false
};
/**
 * Masterplan simple event tracker
 * api based on: mixpanel js library
 * code based on: https://github.com/benawad/expo-mixpanel-analytics/blob/master/src/index.ts
 *
 * @returns {object} instance of tracker
 */

export function eventTracker() {
  var queue = [];
  var config = DEFAULT_CONFIG;
  var isReady = false;
  var isDisable = false;
  var distinctId = null;
  var onError = noop;
  var persistence = {
    props: {
      $lib_version: '10.1.0'
    },
    timers: {}
  };
  /**
   * Make event object
   *
   * @param {string} name - name of the event
   * @param {object} props - properties of the event
   *
   * @returns {object} event object
   */

  function get_event(name, props) {
    return {
      event: name,
      id: createUuid(),
      timestamp: formatDatetimeServer(new Date()),
      properties: _objectSpread(_objectSpread(_objectSpread({
        user_id: distinctId
      }, persistence.props), props || {}), {}, {
        token: config.token,
        distinct_id: distinctId
      })
    };
  }
  /**
   * Send network request with the data
   *
   * @param {('track'|'engage')} type - type of request
   * @param {object} data - data to send
   *
   * @returns {Promise} response
   */


  function push(type, data) {
    if (isDisable) {
      return Promise.resolve();
    }

    if (config.debug) {
      // eslint-disable-next-line no-console
      console.log('EVENT TRACKER REQUEST:', {
        type: type,
        data: data
      });
    }

    return axios.get("".concat(config.apiUrl, "/").concat(type, "?data=").concat(toBase64(data))).catch(onError);
  }
  /**
   * Send event
   *
   * @param {object} [params] - props
   * @param {string} [params.name] - name of the event
   * @param {object} [params.props] - data to send with the event
   *
   * @returns {undefined}
   */


  function pushEvent(_ref) {
    var name = _ref.name,
        props = _ref.props;
    return push('track', get_event(name, props));
  }
  /**
   * Send profile
   *
   * @param {object} data - data to send
   *
   * @returns {Promise} response
   */


  function pushProfile(data) {
    return push('engage', data);
  }
  /**
   * Push all the events in the queue
   */


  function flush() {
    if (isReady && distinctId) {
      while (queue.length) {
        var event = queue.pop();
        pushEvent(event);
      }
    }
  }

  function people(operation) {
    return function (props) {
      if (isReady && distinctId) {
        var data = {
          $token: config.token,
          $distinct_id: distinctId
        };
        data["$".concat(operation)] = props;
        pushProfile(data);
      }
    };
  }
  /**
   * Update the configuration
   *
   * @param {object} initConfig - configuration object
   */


  function set_config(initConfig) {
    config = _objectSpread(_objectSpread({}, config), initConfig);
  }

  function get_config() {
    return config;
  }
  /**
   * Initialize tracking
   *
   * @param {string} initToken - your token
   */


  function init(initToken) {
    config = _objectSpread(_objectSpread({}, config), {}, {
      token: initToken
    });
    isReady = true;
    flush();
  }
  /**
   * Track an event
   *
   * @param {string} name - the name of the event
   * @param {object} props - a set of properties to include with the event you're sending
   */


  function track(name, props) {
    queue.push({
      name: name,
      props: props
    });
    flush();
  }
  /**
   * Identify a user with a unique ID to track user activity across
   * devices, tie a user to their events.
   *
   * @param {string} uniqueId - a string that uniquely identifies a user
   */


  function identify(uniqueId) {
    distinctId = uniqueId;
  }

  function get_distinct_id() {
    return distinctId;
  }
  /**
   * Register a set of super properties, which are included with all
   * events.
   *
   * @param {object} props - an associative array of properties to store about the user
   *
   * @returns {undefined}
   */


  function register(props) {
    persistence.props = _objectSpread(_objectSpread({}, persistence.props), props);
  }
  /**
   * Delete a super property stored with the current user.
   *
   * @param {string} property - the name of the super property to remove
   */


  function unregister(property) {
    persistence.props = omit(property, persistence.props);
  }
  /**
   * Time an event by including the time between this call and a
   * later 'track' call for the same event in the properties sent
   * with the event.
   *
   * @param {string} name - the name of the event
   */


  function time_event(name) {
    persistence.timers[name] = new Date().getTime();
  }
  /**
   * The alias method creates an alias which Mixpanel will use to
   * remap one id to another. Multiple aliases can point to the
   * same identifier.
   *
   * @param {string} aliasName - alias A unique identifier that you want to use for this user in the future
   * @param {string} [original] - the current identifier being used for this user
   */


  function alias(aliasName, original) {
    var id = original || get_distinct_id();
    register({
      __alias: alias
    });
    track('$create_alias', {
      alias: aliasName,
      distinct_id: id
    });
    identify(aliasName);
  }
  /**
   * Clears distinct_id
   */


  function reset() {
    identify(null);
  }
  /**
   * Disable tracking
   */


  function disable() {
    isDisable = true;
  }
  /**
   * Set function to call on error
   *
   * @param {Function} callback - callback function
   */


  function on_error(callback) {
    onError = callback;
  }

  return {
    set_config: set_config,
    get_config: get_config,
    init: init,
    disable: disable,
    track: track,
    get_event: get_event,
    time_event: time_event,
    identify: identify,
    get_distinct_id: get_distinct_id,
    register: register,
    alias: alias,
    unregister: unregister,
    reset: reset,
    on_error: on_error,
    people: {
      set: people('set'),
      set_once: people('set_once'),
      unset: people('unset'),
      increment: people('increment'),
      append: people('append'),
      union: people('union'),
      delete_user: people('delete_user')
    },
    persistence: persistence
  };
}
export default eventTracker();